import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Paperclip, X, Search, Trash2 } from "lucide-react";
import api from "../../api/api";

export default function Attachments({ mdlId, primeId, primeName, uploadedBy }) {
  const [attachments, setAttachments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [remarks, setRemarks] = useState("");

  const modalRef = useRef(null);

  useEffect(() => {
    if (mdlId && primeId) fetchFiles();
  }, [mdlId, primeId]);

  useEffect(() => {
    if (isOpen) fetchFiles();
  }, [isOpen]);

  const fetchFiles = async () => {
    if (!mdlId || !primeId) return;
    try {
      const res = await api.GetAttachments(mdlId, primeId);
      const table = res?.Attachments?.Table || res?.Attachments || [];
      setAttachments(Array.isArray(table) ? table : []);
    } catch (err) {
      console.error("❌ Error loading attachments:", err);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert("Please select files first!");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("MdlId", mdlId);
    formData.append("PrimeId", primeId);
    formData.append("PrimeName", primeName);
    formData.append("UploadedBy", uploadedBy);
    formData.append("Remarks", remarks);
    selectedFiles.forEach((file) => formData.append("files", file));

    try {
      const res = await api.UploadAttachment(formData);
      if (res?.success) {
        setSelectedFiles([]);
        setRemarks("");
        await fetchFiles();
      } else alert("⚠️ Upload failed. Please try again.");
    } catch (err) {
      console.error("❌ Upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAttachment = async (file) => {
    const attachmentId =
      file.Id ||
      file.ID ||
      file.AttchId ||
      file.AttachId ||
      0;

    if (!attachmentId) {
      alert("Invalid Attachment Id");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this attachment?"
    );

    if (!confirmDelete) return;

    try {
      const res = await api.DeleteAttachment(attachmentId);
      const result = res?.data || res;

      if (
        result?.Status === 0 ||
        result?.status === 0 ||
        result?.success === false
      ) {
        alert(
          result?.Message ||
            result?.message ||
            "Failed to delete attachment."
        );
        return;
      }

      alert(
        result?.Message ||
          result?.message ||
          "Attachment deleted successfully."
      );

      await fetchFiles();
    } catch (err) {
      console.error("❌ Delete attachment failed:", err);

      alert(
        err?.response?.data?.Message ||
          err?.response?.data?.message ||
          "Failed to delete attachment."
      );
    }
  };

  useEffect(() => {
    const handleEsc = (e) => e.key === "Escape" && setIsOpen(false);
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const filteredAttachments = attachments.filter((file) =>
    (file.FileName || "").toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  return (
    <>
      {/* 📎 Attachments Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
      >
        <Paperclip size={18} />
        <span className="text-sm font-semibold tracking-wide">
          Attachments
          {attachments.length > 0 && (
            <span className="ml-2 bg-white/25 px-2 py-0.5 rounded-full text-xs font-normal">
              {attachments.length}
            </span>
          )}
        </span>
      </button>

      {/* 🧩 Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />

            {/* Modal */}
            <motion.div
              ref={modalRef}
              drag
              dragConstraints={{ left: -300, right: 300, top: -200, bottom: 200 }}
              dragElastic={0.15}
              className="fixed top-1/2 left-1/2 w-full max-w-3xl h-[80vh] bg-white/95 border border-gray-200 rounded-2xl shadow-2xl p-6 z-50 flex flex-col backdrop-blur-md"
              initial={{ opacity: 0, scale: 0.9, x: "-50%", y: "-50%" }}
              animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 250, damping: 22 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-4 border-b pb-3">
                <h2 className="text-2xl font-semibold text-gray-800 flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Paperclip size={22} className="text-blue-600" />
                    Attachments
                  </div>
                  <div className="flex items-center gap-2 ml-8">
                    <span className="text-xl text-gray-800 font-semibold">
                      {primeName}
                    </span>
                    <span className="text-sm text-gray-500 font-normal">
                      ({attachments.length})
                    </span>
                  </div>
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <X size={22} />
                </button>
              </div>

              {/* Upload Section */}
              <div className="mb-4 bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-4 shadow-inner">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-700 border rounded-lg cursor-pointer 
                    file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0
                    file:text-sm file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                  />
                  <button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className={`${
                      isUploading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    } text-white px-6 py-2 rounded-lg text-sm font-medium shadow`}
                  >
                    {isUploading ? "Uploading..." : "Upload"}
                  </button>
                </div>

                <textarea
                  placeholder="Add remarks (optional)..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full border rounded-lg p-2 mt-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  rows="2"
                />

                {selectedFiles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1 italic">
                    {selectedFiles.length} file(s) selected
                  </p>
                )}
              </div>

              {/* Search Bar */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                />
              </div>

              {/* File List */}
              <div className="flex-1 overflow-y-auto border-t pt-3 bg-gradient-to-b from-gray-50 to-gray-100 rounded-lg px-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {filteredAttachments.length === 0 ? (
                  <p className="text-sm text-gray-500 italic mt-4 text-center">
                    No matching files found.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {filteredAttachments.map((file, index) => {
                      const name =
                        file.FileName ||
                        file.File_Path ||
                        file.Filename ||
                        file.DocName ||
                        file.OriginalFileName ||
                        "Unnamed File";
                      const path = file.FilePath || file.File_Path || "#";
                      const remarks = file.Remarks || "—";

                      return (
                        <li
                          key={index}
                          className="bg-white hover:bg-gray-50 border border-gray-200 p-3 rounded-xl shadow-sm transition text-sm flex flex-col group"
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2 max-w-[70%]">
                              <a
                                href={path}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:underline font-medium truncate"
                                title={name}
                              >
                                {name}
                              </a>
                              <a
                                href={path}
                                download={name}
                                className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1.5 rounded-full hover:bg-blue-50"
                                title="Download file"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 text-blue-600"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                                  />
                                </svg>
                              </a>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                {file.UploadedDate
                                  ? new Date(file.UploadedDate).toLocaleDateString()
                                  : ""}
                              </span>

                              <button
                                type="button"
                                onClick={() => handleDeleteAttachment(file)}
                                className="p-1.5 text-red-500 transition rounded-full hover:bg-red-50 hover:text-red-700"
                                title="Delete Attachment"
                              >
                                <Trash2 size={17} />
                              </button>
                            </div>
                          </div>

                          {remarks && remarks !== "—" && (
                            <div className="mt-1 text-sm text-gray-600 truncate">
                              📝{" "}
                              <span className="font-medium text-gray-700">
                                {remarks}
                              </span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
